import React, { useCallback, useMemo } from 'react';
import { Project as ProjectModel } from '@material-bank/projects-api';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useProjectsProvider,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import { AnalyticsEventLocation, AnalyticsEventName } from '@mb/lib';
import { ProjectDropdownVariant } from 'components/Cards/ProductCard/ProductCard.types';
import ProjectDropdown from 'components/Cards/ProductCard/ProjectDropdown';
import { useDispatch } from 'react-redux';
import {
  setIsProjectModalOpen,
  setProjectModalEventLocation,
} from '../../redux/sliceCreators/projectsSlice';

interface ProjectMenuDropdownProps {
  entityId: string;
  createProjectButtonLabel: string;
}

const ProjectMenuDropdown: React.FC<ProjectMenuDropdownProps> = ({
  entityId,
  createProjectButtonLabel,
}) => {
  const dispatch = useDispatch();
  const { activeProjects } = useProjectsProvider();
  const { isProjectDataComplete } = useValidateProjectData();
  const { currentProject, setCurrentProjectId } = useCurrentProject();

  const structuredProjects = useMemo(() => {
    return Object.values(activeProjects).map((project) => ({
      id: project.externalId,
      label: project.title,
    }));
  }, [activeProjects]);

  const currentProjectData = useMemo(
    () => ({
      id: currentProject?.externalId || 0,
      label: currentProject?.title ?? 'Select project..',
    }),
    [currentProject],
  );

  const onCreateProjectClick = useCallback(
    (params?: { analyticsEventLocation?: string }) => {
      const { analyticsEventLocation } = params ?? {};

      dispatch(setIsProjectModalOpen(true));

      if (analyticsEventLocation) {
        dispatch(setProjectModalEventLocation(analyticsEventLocation));
      }
    },
    [dispatch],
  );

  const onProjectSelect = useCallback(
    (projectId: number | null) => {
      setCurrentProjectId(projectId);
    },
    [setCurrentProjectId],
  );

  const handleProjectClick = (projectId: number | null) => {
    const project = projectId ? activeProjects[projectId] : null;

    const onLocalCurrentProjectSave = (projectId: number | null) => {
      onProjectSelect(projectId);
    };

    if (project) {
      if (!isProjectDataComplete(project)) {
        projectsEditDialogConfig.openProjectEditDialog({
          projectId: Number(project.externalId),
          eventLocation: AnalyticsEventLocation.QUICK_VIEW,
          successCallback(project: ProjectModel) {
            onLocalCurrentProjectSave?.(project.externalId);
          },
        });
      } else {
        setCurrentProjectId?.(project.externalId);

        mitGTM.sendEvent({
          event: AnalyticsEventName.SET_PROJECT,
          event_location: AnalyticsEventLocation.QUICK_VIEW,
          action: 'Select',
        });
      }
    }
  };

  return (
    <ProjectDropdown
      shouldShow
      entityId={entityId}
      maxWidth={300}
      isContained={false}
      onProjectClick={handleProjectClick}
      onCreateProject={onCreateProjectClick}
      selectedProject={currentProjectData}
      projects={structuredProjects}
      variant={ProjectDropdownVariant.GHOST}
      preLabel={''}
      createProjectButtonLabel={createProjectButtonLabel}
      hideAddToBag
      minWidth={120}
    />
  );
};

export { ProjectMenuDropdown };
